import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, FileTextIcon } from 'lucide-react';
import { BillingModal } from './BillingModal';
import { TransactionFormModal } from './TransactionFormModal';
import { TransactionSuccessModal } from './TransactionSuccessModal';
import { useAuth } from '../contexts/AuthContext';
import { useTransactions } from '../contexts/TransactionContext';
type CommissionedParty = 'seller' | 'buyer' | 'both';
type VATOption = 'included' | 'additional';
interface SaleCommissionCalculatorProps {
  onNavigateToTransactions?: () => void;
}
export function SaleCommissionCalculator({
  onNavigateToTransactions
}: SaleCommissionCalculatorProps) {
  const [propertyValue, setPropertyValue] = useState<string>('');
  const [commissionedParty, setCommissionedParty] = useState<CommissionedParty>('both');
  const [sellerCommission, setSellerCommission] = useState<string>('2');
  const [buyerCommission, setBuyerCommission] = useState<string>('2');
  const [vatOption, setVATOption] = useState<VATOption>('additional');
  const [sellerVATOption, setSellerVATOption] = useState<VATOption>('additional');
  const [buyerVATOption, setBuyerVATOption] = useState<VATOption>('additional');
  const [showResult, setShowResult] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const {
    user
  } = useAuth();
  const {
    addTransaction
  } = useTransactions();
  const calculateCommission = () => {
    if (parseFloat(propertyValue) > 0) {
      setShowResult(true);
    }
  };
  const handleAddTransaction = () => {
    const transaction = {
      type: 'sale' as const,
      transactionValue: propertyNum,
      agreedCommission: totalCommission,
      commissionedParty,
      clientName: 'Client Simulare',
      clientEmail: '',
      clientPhone: '',
      propertyAddress: 'Adresă de completat',
      propertyDetails: `Vânzare - Simulare`,
      notes: 'Tranzacție adăugată din simulator',
      sellerCommission: sellerCommissionAmount,
      buyerCommission: buyerCommissionAmount,
      sellerVAT,
      buyerVAT,
      sellerTotal,
      buyerTotal,
      vatOption,
      sellerVATOption,
      buyerVATOption,
      agentName: user?.fullName || 'Agent'
    };
    addTransaction(transaction);
    setShowSuccessModal(true);
  };
  const handleViewTransactions = () => {
    setShowSuccessModal(false);
    if (onNavigateToTransactions) {
      onNavigateToTransactions();
    }
  };
  const propertyNum = parseFloat(propertyValue) || 0;
  const sellerCommNum = Math.max(parseFloat(sellerCommission) || 0, 2);
  const buyerCommNum = Math.max(parseFloat(buyerCommission) || 0, 2);
  let sellerCommissionAmount = 0;
  let buyerCommissionAmount = 0;
  let sellerVAT = 0;
  let buyerVAT = 0;
  let sellerTotal = 0;
  let buyerTotal = 0;
  let totalCommission = 0;
  let totalVAT = 0;
  let finalTotal = 0;
  if (commissionedParty === 'seller') {
    sellerCommissionAmount = propertyNum * sellerCommNum / 100;
    totalCommission = sellerCommissionAmount;
    if (vatOption === 'included') {
      // Reverse calculate: if VAT is included, the total is 121% (100% + 21%)
      const baseCommission = totalCommission / 1.21;
      totalVAT = totalCommission - baseCommission;
      finalTotal = totalCommission;
      sellerCommissionAmount = baseCommission;
    } else {
      totalVAT = totalCommission * 0.21;
      finalTotal = totalCommission + totalVAT;
    }
  } else if (commissionedParty === 'buyer') {
    buyerCommissionAmount = propertyNum * buyerCommNum / 100;
    totalCommission = buyerCommissionAmount;
    if (vatOption === 'included') {
      // Reverse calculate: if VAT is included, the total is 121% (100% + 21%)
      const baseCommission = totalCommission / 1.21;
      totalVAT = totalCommission - baseCommission;
      finalTotal = totalCommission;
      buyerCommissionAmount = baseCommission;
    } else {
      totalVAT = totalCommission * 0.21;
      finalTotal = totalCommission + totalVAT;
    }
  } else {
    // Calcul separat pentru fiecare parte cu opțiuni TVA separate
    sellerCommissionAmount = propertyNum * sellerCommNum / 100;
    buyerCommissionAmount = propertyNum * buyerCommNum / 100;
    if (sellerVATOption === 'included') {
      const baseCommission = sellerCommissionAmount / 1.21;
      sellerVAT = sellerCommissionAmount - baseCommission;
      sellerTotal = sellerCommissionAmount;
      sellerCommissionAmount = baseCommission;
    } else {
      sellerVAT = sellerCommissionAmount * 0.21;
      sellerTotal = sellerCommissionAmount + sellerVAT;
    }
    if (buyerVATOption === 'included') {
      const baseCommission = buyerCommissionAmount / 1.21;
      buyerVAT = buyerCommissionAmount - baseCommission;
      buyerTotal = buyerCommissionAmount;
      buyerCommissionAmount = baseCommission;
    } else {
      buyerVAT = buyerCommissionAmount * 0.21;
      buyerTotal = buyerCommissionAmount + buyerVAT;
    }
    totalCommission = sellerCommissionAmount + buyerCommissionAmount;
    totalVAT = sellerVAT + buyerVAT;
    finalTotal = sellerTotal + buyerTotal;
  }
  return <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-6654">
        {/* Calculator Input */}
        <div className="bg-white rounded-2xl p-8 shadow-xl" data-id="element-6655">
          <div className="flex items-center space-x-3 mb-6" data-id="element-6656">
            <TrendingUpIcon className="w-8 h-8 text-[#FF6B35]" data-id="element-6657" />
            <h2 className="text-2xl font-bold text-[#132C53]" data-id="element-6658">
              Calculator Vânzare
            </h2>
          </div>
          <div className="space-y-6" data-id="element-6659">
            {/* Property Value */}
            <div data-id="element-6660">
              <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6661">
                Valoare imobil tranzacționat (€)
              </label>
              <input type="number" value={propertyValue} onChange={e => setPropertyValue(e.target.value)} className="w-full text-3xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#FF6B35]" placeholder="150000" min="80000" data-id="element-6662" />
            </div>
            {/* Commissioned Party */}
            <div data-id="element-6663">
              <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6664">
                Părți comisionate
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-6665">
                {[{
                value: 'seller',
                label: 'Vânzător'
              }, {
                value: 'buyer',
                label: 'Cumpărător'
              }, {
                value: 'both',
                label: 'Ambele'
              }].map(option => <button key={option.value} onClick={() => setCommissionedParty(option.value as CommissionedParty)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${commissionedParty === option.value ? 'border-[#FF6B35] bg-[#FF6B35] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6666">
                    {option.label}
                  </button>)}
              </div>
            </div>
            {/* Seller Commission */}
            {(commissionedParty === 'seller' || commissionedParty === 'both') && <div className="space-y-4" data-id="element-6667">
                <div data-id="element-6668">
                  <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6669">
                    Comision vânzător (% - minim 2%)
                  </label>
                  <input type="number" value={sellerCommission} onChange={e => setSellerCommission(e.target.value)} className="w-full text-2xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#FF6B35]" placeholder="2" min="2" max="100" step="0.1" data-id="element-6670" />
                </div>
                {commissionedParty === 'both' && <div data-id="element-6671">
                    <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6672">
                      Opțiune TVA vânzător
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-6673">
                      {[{
                  value: 'included',
                  label: 'TVA inclus'
                }, {
                  value: 'additional',
                  label: '+ TVA (21%)'
                }].map(option => <button key={option.value} onClick={() => setSellerVATOption(option.value as VATOption)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${sellerVATOption === option.value ? 'border-[#FF6B35] bg-[#FF6B35] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6674">
                          {option.label}
                        </button>)}
                    </div>
                  </div>}
              </div>}
            {/* Buyer Commission */}
            {(commissionedParty === 'buyer' || commissionedParty === 'both') && <div className="space-y-4" data-id="element-6675">
                <div data-id="element-6676">
                  <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6677">
                    Comision cumpărător (% - minim 2%)
                  </label>
                  <input type="number" value={buyerCommission} onChange={e => setBuyerCommission(e.target.value)} className="w-full text-2xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#FF6B35]" placeholder="2" min="2" max="100" step="0.1" data-id="element-6678" />
                </div>
                {commissionedParty === 'both' && <div data-id="element-6679">
                    <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6680">
                      Opțiune TVA cumpărător
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-6681">
                      {[{
                  value: 'included',
                  label: 'TVA inclus'
                }, {
                  value: 'additional',
                  label: '+ TVA (21%)'
                }].map(option => <button key={option.value} onClick={() => setBuyerVATOption(option.value as VATOption)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${buyerVATOption === option.value ? 'border-[#FF6B35] bg-[#FF6B35] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6682">
                          {option.label}
                        </button>)}
                    </div>
                  </div>}
              </div>}
            {/* VAT Option - Only for single party */}
            {commissionedParty !== 'both' && <div data-id="element-6683">
                <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6684">
                  Opțiune TVA
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-6685">
                  {[{
                value: 'included',
                label: 'TVA inclus'
              }, {
                value: 'additional',
                label: '+ TVA (21%)'
              }].map(option => <button key={option.value} onClick={() => setVATOption(option.value as VATOption)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${vatOption === option.value ? 'border-[#FF6B35] bg-[#FF6B35] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6686">
                      {option.label}
                    </button>)}
                </div>
              </div>}
            <button onClick={calculateCommission} disabled={!propertyValue || parseFloat(propertyValue) <= 0} className="w-full bg-gradient-to-r from-[#FF6B35] to-[#e55a2b] text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-shadow disabled:opacity-50" data-id="element-6687">
              Calculează comision
            </button>
          </div>
        </div>
        {/* Results */}
        <div className="space-y-6" data-id="element-6688">
          {showResult && propertyNum > 0 ? <>
              <div className="bg-white rounded-2xl p-8 shadow-xl" data-id="element-6689">
                <h2 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-6690">
                  Rezultat calcul
                </h2>
                <div className="space-y-4" data-id="element-6691">
                  <div className="bg-gray-50 rounded-xl p-4" data-id="element-6692">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-6693">Valoare imobil</p>
                    <p className="text-2xl font-bold text-gray-700" data-id="element-6694">
                      {propertyNum.toLocaleString('ro-RO')}€
                    </p>
                  </div>
                  {commissionedParty === 'both' ? <>
                      <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200" data-id="element-6695">
                        <p className="text-sm text-gray-600 mb-3 font-semibold" data-id="element-6696">
                          Calcul detaliat pe părți:
                        </p>
                        <div className="space-y-4" data-id="element-6697">
                          {/* Vânzător */}
                          <div className="pb-3 border-b border-purple-200" data-id="element-6698">
                            <div className="flex justify-between items-center mb-2" data-id="element-6699">
                              <span className="text-sm font-semibold text-gray-700" data-id="element-6700">
                                Comision vânzător ({sellerCommNum}%)
                              </span>
                              <span className="font-bold text-purple-700" data-id="element-6701">
                                {sellerCommissionAmount.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                            {sellerVATOption === 'additional' && <div className="flex justify-between items-center text-sm" data-id="element-6702">
                                <span className="text-gray-600" data-id="element-6703">
                                  + TVA (21%)
                                </span>
                                <span className="font-semibold text-purple-600" data-id="element-6704">
                                  {sellerVAT.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                  €
                                </span>
                              </div>}
                            {sellerVATOption === 'included' && <div className="text-sm space-y-1 mb-2" data-id="element-6705">
                                <div className="flex justify-between items-center text-gray-600" data-id="element-6706">
                                  <span data-id="element-6707">TVA inclus (21%):</span>
                                  <span className="font-semibold text-purple-600" data-id="element-6708">
                                    {sellerVAT.toLocaleString('ro-RO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                                    €
                                  </span>
                                </div>
                              </div>}
                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-purple-100" data-id="element-6709">
                              <span className="text-sm font-bold text-gray-700" data-id="element-6710">
                                Total vânzător
                              </span>
                              <span className="font-bold text-purple-800" data-id="element-6711">
                                {sellerTotal.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                          </div>
                          {/* Cumpărător */}
                          <div data-id="element-6712">
                            <div className="flex justify-between items-center mb-2" data-id="element-6713">
                              <span className="text-sm font-semibold text-gray-700" data-id="element-6714">
                                Comision cumpărător ({buyerCommNum}%)
                              </span>
                              <span className="font-bold text-purple-700" data-id="element-6715">
                                {buyerCommissionAmount.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                            {buyerVATOption === 'additional' && <div className="flex justify-between items-center text-sm" data-id="element-6716">
                                <span className="text-gray-600" data-id="element-6717">
                                  + TVA (21%)
                                </span>
                                <span className="font-semibold text-purple-600" data-id="element-6718">
                                  {buyerVAT.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                  €
                                </span>
                              </div>}
                            {buyerVATOption === 'included' && <div className="text-sm space-y-1 mb-2" data-id="element-6719">
                                <div className="flex justify-between items-center text-gray-600" data-id="element-6720">
                                  <span data-id="element-6721">TVA inclus (21%):</span>
                                  <span className="font-semibold text-purple-600" data-id="element-6722">
                                    {buyerVAT.toLocaleString('ro-RO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                                    €
                                  </span>
                                </div>
                              </div>}
                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-purple-100" data-id="element-6723">
                              <span className="text-sm font-bold text-gray-700" data-id="element-6724">
                                Total cumpărător
                              </span>
                              <span className="font-bold text-purple-800" data-id="element-6725">
                                {buyerTotal.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </> : <>
                      <div className="bg-blue-50 rounded-xl p-4" data-id="element-6726">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-6727">
                          Comision fără TVA
                        </p>
                        <p className="text-2xl font-bold text-[#357BEB]" data-id="element-6728">
                          {(commissionedParty === 'seller' ? sellerCommissionAmount : buyerCommissionAmount).toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                          €
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-4" data-id="element-6729">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-6730">
                          {vatOption === 'included' ? 'TVA inclus (21%)' : 'TVA (21%)'}
                        </p>
                        <p className="text-2xl font-bold text-orange-600" data-id="element-6731">
                          {totalVAT.toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                          €
                        </p>
                      </div>
                    </>}
                  {vatOption === 'additional' && commissionedParty !== 'both' && <div className="bg-orange-50 rounded-xl p-4" data-id="element-6732">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-6733">TVA (21%)</p>
                        <p className="text-2xl font-bold text-orange-600" data-id="element-6734">
                          {totalVAT.toLocaleString('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                          €
                        </p>
                      </div>}
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200" data-id="element-6735">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-6736">
                      Total general de facturat
                    </p>
                    <p className="text-3xl font-bold text-green-700" data-id="element-6737">
                      {finalTotal.toLocaleString('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      €
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200" data-id="element-6738">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-6739">
                      Sumă eligibilă pentru comisionare (fără TVA)
                    </p>
                    <p className="text-3xl font-bold text-blue-700" data-id="element-6740">
                      {totalCommission.toLocaleString('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      €
                    </p>
                  </div>
                </div>
              </div>
              {user ? <button onClick={handleAddTransaction} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-all flex items-center justify-center space-x-2" data-id="element-6741">
                  <FileTextIcon className="w-5 h-5" data-id="element-6742" />
                  <span data-id="element-6743">Adaugă tranzacție</span>
                </button> : <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 text-center" data-id="element-6744">
                  <p className="text-gray-700 mb-4" data-id="element-6745">
                    Trebuie să fii autentificat pentru a adăuga tranzacții
                  </p>
                  <a href="/auth/login" className="inline-flex items-center space-x-2 bg-[#357BEB] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-6746">
                    <span data-id="element-6747">Loghează-te pentru a adăuga tranzacții</span>
                  </a>
                </div>}
            </> : <div className="bg-white rounded-2xl p-12 shadow-xl text-center" data-id="element-6748">
              <div className="text-6xl mb-4" data-id="element-6749">🏢</div>
              <h3 className="text-xl font-bold text-[#132C53] mb-2" data-id="element-6750">
                Completează datele pentru calcul
              </h3>
            </div>}
        </div>
      </div>
      {showTransactionForm && user && <TransactionFormModal transactionType="sale" transactionValue={propertyNum} agreedCommission={totalCommission} commissionedParty={commissionedParty} sellerCommission={sellerCommissionAmount} buyerCommission={buyerCommissionAmount} sellerVAT={sellerVAT} buyerVAT={buyerVAT} sellerTotal={sellerTotal} buyerTotal={buyerTotal} sellerVATOption={sellerVATOption} buyerVATOption={buyerVATOption} vatOption={vatOption} onClose={() => setShowTransactionForm(false)} onBack={() => setShowTransactionForm(false)} data-id="element-6751" />}
      <TransactionSuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} onViewTransactions={handleViewTransactions} data-id="element-6752" />
    </>;
}